import BgImg from '@assets/jpg/signup-bgs/step-2.jpeg';
import { Button, Form, TextField } from '@components/ui';
import PasswordStrength from '@components/ui/password-strength';
import { AuthTitleSC } from '@pages/auth/auth.styled';
import AuthLayout from '@pages/auth/components/auth-layout';
import Header from '@pages/auth/components/header';
import { recoveryPassword } from '@services/external/auth.api';
import JwtDecode from 'jwt-decode';
import moment from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useTimer } from 'use-timer';
import { initialValues, TFormValues, validationSchema } from './reset-password-new.schema';

const ResetPasswordNew: React.FunctionComponent = () => {
  const { state } = useLocation<{ recoveryToken: string }>();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const recoveryToken = state?.recoveryToken;

  const [loading, setLoading] = React.useState(false);

  const onSubmit = React.useCallback(
    async (values: TFormValues) => {
      setLoading(true);
      try {
        await recoveryPassword(recoveryToken, values.password);
        history.replace('/reset-password-status', {
          status: 'success',
        });
      } catch (error: any) {
        history.replace('/reset-password-status', {
          status: 'error',
          message: (error.data && error.data.message_code) ?? 'general',
        });
      } finally {
        setLoading(false);
      }
    },
    [recoveryToken],
  );

  const timeForSubmit = React.useMemo(() => {
    try {
      const decoded = JwtDecode<{ exp: number }>(recoveryToken);
      const diff = moment.unix(decoded.exp).diff(moment(), 'second');
      return diff < 0 ? 0 : diff;
    } catch (error) {
      return 0;
    }
  }, []);

  React.useEffect(() => {
    if (!recoveryToken || timeForSubmit === 0) {
      history.replace('/login');
    }
  }, [recoveryToken, timeForSubmit]);

  const { time } = useTimer({
    initialTime: timeForSubmit,
    timerType: 'DECREMENTAL',
    autostart: true,
    endTime: 0,
    onTimeOver: () => {
      history.replace('/reset-password-status', {
        status: 'error',
        message: 'Invalid_token',
      });
    },
  });

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <AuthLayout backgroundImage={BgImg}>
      <div className="form-wrapper forgotten">
        <Header textId="forgotten_password_new.title" />
        <Form {...formOptions} className="form" showFeedback={false} showLabel={false}>
          <AuthTitleSC>
            <FormattedMessage id="forgotten_password_new.title" />
          </AuthTitleSC>
          <p className="form-description">
            <FormattedMessage id="forgotten_password_new.description" />
          </p>
          <Form.Field name="password" label={formatMessage({ id: 'reg.step_1.password' })}>
            <TextField
              placeholder={`${formatMessage({ id: 'reg.step_1.password' })}*`}
              type="password"
              autoComplete="new-password"
            />
          </Form.Field>
          <Form.Custom>
            {(f) => <PasswordStrength password={f.values.password} style={{ marginBottom: 8 }} />}
          </Form.Custom>
          <Form.Field name="repeated" label={formatMessage({ id: 'reg.step_1.repeated' })}>
            <TextField
              placeholder={`${formatMessage({ id: 'reg.step_1.repeated' })}*`}
              type="password"
              autoComplete="new-password"
            />
          </Form.Field>
          <Button type="submit" loading={loading} style={{ marginTop: 16, marginBottom: 16 }}>
            <FormattedMessage id="forgotten_password_new.submit" />
            {` (${Math.floor((time % 3600) / 60)
              .toString()
              .padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')})`}
          </Button>
        </Form>
      </div>
    </AuthLayout>
  );
};

export default React.memo(ResetPasswordNew);
